import { Skeleton } from "antd";
import React, { memo, useMemo } from "react";

interface VideoElementProps {
  src: string;
  videoRef: React.RefObject<HTMLVideoElement | null>;
  isLoaded: boolean;
  setIsLoaded: (loaded: boolean) => void;
  fitContent?: boolean;
  maxHeight?: string | number;
  muted?: boolean;
  autoPlay?: boolean;
  loop?: boolean;
  poster?: string;
}

export const VideoElement = memo(
  ({
    src,
    videoRef,
    isLoaded,
    setIsLoaded,
    fitContent,
    maxHeight,
    muted,
    autoPlay,
    loop,
    poster,
  }: VideoElementProps) => {
    const videoProps = useMemo(
      () => ({
        ...(muted && { muted }),
        ...(autoPlay && { autoPlay }),
        ...(loop && { loop }),
        poster,
        preload: "metadata" as const,
      }),
      [poster, muted, autoPlay, loop]
    );

    const videoStyle: React.CSSProperties = useMemo(() => {
      const style: React.CSSProperties = { objectFit: "contain" };
      if (fitContent) {
        style.maxWidth = "100%";
        style.maxHeight = "100%";
      } else if (maxHeight) {
        style.maxHeight = maxHeight;
      }
      return style;
    }, [fitContent, maxHeight]);

    return (
      <>
        {!isLoaded && (
          <div className="overflow-hidden w-full h-full">
            <div className="w-full h-full flex items-center justify-center aspect-video">
              <Skeleton.Node
                active
                className="block! w-full! h-full! aspect-video! bg-background-body/50!"
              />
            </div>
          </div>
        )}
        <video
          onLoadedMetadata={() => setIsLoaded(true)}
          onError={(e) => console.error("video load error:", e)}
          suppressHydrationWarning
          playsInline
          ref={videoRef}
          slot="media"
          src={src}
          className="w-full h-full object-contain"
          style={videoStyle}
          {...videoProps}
        ></video>
      </>
    );
  }
);

VideoElement.displayName = "VideoElement";
